/**
 * Feature flags client — staged rollout gates.
 *
 * Fetches /api/feature-flags/my once per session and caches the result.
 * Gated menu items are hidden until flags are known (fail closed), so a
 * piloted feature never flashes for users who shouldn't see it.
 *
 * To gate a new feature: add a row to the feature_flags table (migration or
 * the PMO > Development > Feature Flags page) and list its menu item id in
 * FLAG_GATED_MENU_ITEMS below. Delete the row once the feature is fully GA.
 */

import { authenticatedApiCall } from './apiClient';

// Menu item ids gated by a feature flag with the same key.
// Unfinished / empty dashboards stay in Pilot (builder only) until Released on
// PMO > Development > Feature Flags — see that page's Help.
export const FLAG_GATED_MENU_ITEMS = [
  // HSE
  'safety-training-db',
  'safety-jha-jsa',
  'safety-toolbox-talk',
  'safety-daily-inspection',
  'safety-ptw-piping-mechanical',
  'safety-hot-work-permit',
  'safety-incident-near-miss',
  'safety-certificates-db',
  // Training (Coming Soon stubs)
  'training-lgl-portal',
  'training-sharepoint',
  'training-sharepoint-naming',
  'training-sharepoint-templates',
  'training-build',
  'training-shortcuts',
  'training-mouse-gestures',
  'training-question-to-admin',
  // Estimation stubs
  'material-index',
  'estimation-piping-convertors',
  'shop-field-ratio',
  'estimation-piping-lt-size',
  'estimation-piping-lt-material',
  'estimation-piping-lt-schedule',
  'estimation-piping-lt-length',
  // Project Technical / Material stubs
  'technical-files',
  'technical-reports',
  'technical-bridge',
  'technical-correspondence',
  'technical-closeout',
  // Forecasting WIP stubs
  'forecasting-budget',
  'forecasting-actuals-accruals',
  'forecasting-po',
  'forecasting-invoices',
  'forecasting-commitments',
  'forecasting-fwo',
  'forecasting-co',
  'forecasting-ccr',
  'forecasting-cash-flow',
  'forecasting-forecast',
  'forecasting-sop',
  // Processes stubs
  'lgl-portal-tour',
  'processes-suggestion-box',
];

// Who can see/manage the Feature Flags page (the builder) — deliberately NOT
// all superadmins, so nobody else can release a piloted feature early.
// Keep in sync with FLAG_ADMINS in src/utils/featureFlags.js (backend).
export const FLAG_ADMINS = ['yrezaei@laframboise.net'];
export const isFlagAdmin = (email) => FLAG_ADMINS.includes(String(email || '').toLowerCase());

let _flagsPromise = null;

export const getMyFeatureFlags = (instance, accounts) => {
  if (!_flagsPromise) {
    _flagsPromise = authenticatedApiCall(instance, accounts, 'get', '/feature-flags/my')
      .then((res) => res.data?.flags || {})
      .catch((err) => {
        console.error('Failed to load feature flags:', err);
        _flagsPromise = null; // allow a retry on next call
        return null;          // null = unknown -> gated items stay hidden
      });
  }
  return _flagsPromise;
};

export const clearFeatureFlagsCache = () => { _flagsPromise = null; };
